import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Settings, Save, Globe, Palette, Mail, Share2, LayoutGrid, User, Monitor, Shield, CreditCard, Users, Key } from 'lucide-react';
import { toast } from 'sonner';
import ProfileSetting from '../../components/settings/ProfileSetting';
import AppearanceSetting from '../../components/settings/AppearanceSetting';
import AccountSetting from '../../components/settings/AccountSetting';
import BillingSetting from '../../components/settings/BillingSetting';
import TeamSetting from '../../components/settings/TeamSetting';
import IntegrationsSetting from '../../components/settings/IntegrationsSetting';
import AppStoreReviewSetting from '../../components/settings/AppStoreReviewSetting';

const DIST_HOME_DEFAULT = {
  show_hero: true,
  show_secondary_strip: true,
  show_latest: true,
  show_trending_sidebar: true,
  show_category_rails: true,
  category_rails_count: 3,
  hero_source: 'featured',
  latest_limit: 8,
  trending_limit: 5,
};

const DIST_EDITIONS_DEFAULT = [
  { id: 'global', name: 'International', slug: 'global' },
  { id: 'uk', name: 'United Kingdom', slug: 'uk' },
  { id: 'us', name: 'United States', slug: 'us' },
  { id: 'eu', name: 'Europe', slug: 'eu' },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [homeLayoutJson, setHomeLayoutJson] = useState('');
  const [editionsCatalogJson, setEditionsCatalogJson] = useState('');
  const [cdnBaseUrl, setCdnBaseUrl] = useState('');

  useEffect(() => { loadSettings(); }, []);

  useEffect(() => {
    if (!settings) return;
    const hl = settings.home_layout && typeof settings.home_layout === 'object' ? settings.home_layout : DIST_HOME_DEFAULT;
    const ec = Array.isArray(settings.editions_catalog) ? settings.editions_catalog : DIST_EDITIONS_DEFAULT;
    setHomeLayoutJson(JSON.stringify(hl, null, 2));
    setEditionsCatalogJson(JSON.stringify(ec, null, 2));
    setCdnBaseUrl(settings.cdn_base_url || '');
  }, [settings]);

  const loadSettings = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await settingsAPI.get();
      setSettings(res.data);
    } catch {
      if (!quiet) toast.error('Failed to load settings');
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    let home_layout;
    let editions_catalog;
    try {
      home_layout = JSON.parse(homeLayoutJson);
      if (typeof home_layout !== 'object' || home_layout === null || Array.isArray(home_layout)) throw new Error('layout');
    } catch {
      toast.error('Home layout must be valid JSON object');
      setSaving(false);
      return;
    }
    try {
      editions_catalog = JSON.parse(editionsCatalogJson);
      if (!Array.isArray(editions_catalog)) throw new Error('editions');
    } catch {
      toast.error('Editions catalog must be a JSON array');
      setSaving(false);
      return;
    }
    try {
      const { id, _type, created_at, updated_at, updated_by, home_layout: _h, editions_catalog: _e, ...data } = settings;
      await settingsAPI.update({ ...data, home_layout, editions_catalog, cdn_base_url: cdnBaseUrl || null });
      toast.success('Settings saved successfully');
      await loadSettings(true);
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));
  const updateSocial = (key, value) => setSettings(prev => ({ ...prev, social_links: { ...prev.social_links, [key]: value } }));

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>;

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Settings className="w-6 h-6 text-[#B89B5E]" /> Site Settings</h1>
          <p className="text-sm text-[#5B616E] mt-1">Configure your news platform</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <AppStoreReviewSetting />

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="w-full flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="profile"><User className="w-4 h-4 mr-1" /> Profile</TabsTrigger>
          <TabsTrigger value="appearance"><Monitor className="w-4 h-4 mr-1" /> Appearance</TabsTrigger>
          <TabsTrigger value="account"><Shield className="w-4 h-4 mr-1" /> Account</TabsTrigger>
          <TabsTrigger value="billing"><CreditCard className="w-4 h-4 mr-1" /> Billing</TabsTrigger>
          <TabsTrigger value="team"><Users className="w-4 h-4 mr-1" /> Team</TabsTrigger>
          <TabsTrigger value="integrations"><Key className="w-4 h-4 mr-1" /> Integrations</TabsTrigger>
          <TabsTrigger value="general"><Globe className="w-4 h-4 mr-1" /> General</TabsTrigger>
          <TabsTrigger value="branding"><Palette className="w-4 h-4 mr-1" /> Branding</TabsTrigger>
          <TabsTrigger value="seo"><Share2 className="w-4 h-4 mr-1" /> SEO</TabsTrigger>
          <TabsTrigger value="contact"><Mail className="w-4 h-4 mr-1" /> Contact</TabsTrigger>
          <TabsTrigger value="distribution"><LayoutGrid className="w-4 h-4 mr-1" /> Distribution</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <ProfileSetting />
        </TabsContent>

        <TabsContent value="appearance" className="mt-4">
          <AppearanceSetting />
        </TabsContent>

        <TabsContent value="account" className="mt-4">
          <AccountSetting />
        </TabsContent>

        <TabsContent value="billing" className="mt-4">
          <BillingSetting />
        </TabsContent>

        <TabsContent value="team" className="mt-4">
          <TeamSetting />
        </TabsContent>

        <TabsContent value="integrations" className="mt-4">
          <IntegrationsSetting />
        </TabsContent>

        <TabsContent value="general" className="mt-4 space-y-4">
          <Card className="bg-white border-[#E7E0D6]">
            <CardHeader><CardTitle className="text-sm">General Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium mb-1 block">Site Name</label><Input value={settings?.site_name || ''} onChange={e => updateField('site_name', e.target.value)} data-testid="site-name-input" /></div>
              <div><label className="text-sm font-medium mb-1 block">Tagline</label><Input value={settings?.tagline || ''} onChange={e => updateField('tagline', e.target.value)} /></div>
              <div><label className="text-sm font-medium mb-1 block">Description</label><Textarea value={settings?.description || ''} onChange={e => updateField('description', e.target.value)} rows={3} /></div>
              <div><label className="text-sm font-medium mb-1 block">Footer Text</label><Input value={settings?.footer_text || ''} onChange={e => updateField('footer_text', e.target.value)} /></div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="branding" className="mt-4 space-y-4">
          <Card className="bg-white border-[#E7E0D6]">
            <CardHeader><CardTitle className="text-sm">Visual Identity</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm font-medium mb-1 block">Logo URL</label><Input value={settings?.logo_url || ''} onChange={e => updateField('logo_url', e.target.value)} placeholder="https://..." /></div>
                <div><label className="text-sm font-medium mb-1 block">Favicon URL</label><Input value={settings?.favicon_url || ''} onChange={e => updateField('favicon_url', e.target.value)} placeholder="https://..." /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Primary Color</label>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={settings?.primary_color || '#121316'} onChange={e => updateField('primary_color', e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                    <Input value={settings?.primary_color || ''} onChange={e => updateField('primary_color', e.target.value)} className="flex-1" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Accent Color</label>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={settings?.accent_color || '#B89B5E'} onChange={e => updateField('accent_color', e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                    <Input value={settings?.accent_color || ''} onChange={e => updateField('accent_color', e.target.value)} className="flex-1" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="seo" className="mt-4 space-y-4">
          <Card className="bg-white border-[#E7E0D6]">
            <CardHeader><CardTitle className="text-sm">SEO Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium mb-1 block">SEO Title</label><Input value={settings?.seo_title || ''} onChange={e => updateField('seo_title', e.target.value)} /></div>
              <div><label className="text-sm font-medium mb-1 block">SEO Description</label><Textarea value={settings?.seo_description || ''} onChange={e => updateField('seo_description', e.target.value)} rows={2} /></div>
              <div><label className="text-sm font-medium mb-1 block">SEO Keywords (comma separated)</label><Input value={(settings?.seo_keywords || []).join(', ')} onChange={e => updateField('seo_keywords', e.target.value.split(',').map(s => s.trim()))} /></div>
              <div><label className="text-sm font-medium mb-1 block">Analytics ID</label><Input value={settings?.analytics_id || ''} onChange={e => updateField('analytics_id', e.target.value)} placeholder="UA-XXXXXXXXX or G-XXXXXXXXX" /></div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="distribution" className="mt-4 space-y-4">
          <Card className="bg-white border-[#E7E0D6]">
            <CardHeader>
              <CardTitle className="text-sm">Homepage layout & editions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-[#5B616E]">
                Controls public homepage sections (via <code className="text-[11px]">/api/public/home-layout</code>) and the edition picker
                (via <code className="text-[11px]">/api/public/editions</code>). Use valid JSON; invalid JSON blocks save.
              </p>
              <div>
                <label className="text-sm font-medium mb-1 block">CDN Base URL</label>
                <Input value={cdnBaseUrl} onChange={e => setCdnBaseUrl(e.target.value)} placeholder="https://cdn.example.com" data-testid="cdn-base-url-input" />
                <p className="text-[10px] text-[#5B616E] mt-0.5">
                  If set, all relative media asset URLs are prefixed with this CDN origin. Leave blank to serve directly.
                </p>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Home layout (object)</label>
                <Textarea
                  value={homeLayoutJson}
                  onChange={(e) => setHomeLayoutJson(e.target.value)}
                  rows={14}
                  className="font-mono text-xs"
                  data-testid="home-layout-json"
                  placeholder="{}"
                />
                <p className="text-[10px] text-[#5B616E] mt-1">
                  Keys: show_hero, show_secondary_strip, show_latest, show_trending_sidebar, show_category_rails, category_rails_count,
                  hero_source (&quot;featured&quot; | &quot;latest&quot;), latest_limit, trending_limit.
                </p>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Editions catalog (array)</label>
                <Textarea
                  value={editionsCatalogJson}
                  onChange={(e) => setEditionsCatalogJson(e.target.value)}
                  rows={10}
                  className="font-mono text-xs"
                  data-testid="editions-catalog-json"
                  placeholder="[]"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contact" className="mt-4 space-y-4">
          <Card className="bg-white border-[#E7E0D6]">
            <CardHeader><CardTitle className="text-sm">Contact & Social</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm font-medium mb-1 block">Contact Email</label><Input value={settings?.contact_email || ''} onChange={e => updateField('contact_email', e.target.value)} /></div>
                <div><label className="text-sm font-medium mb-1 block">Phone</label><Input value={settings?.phone || ''} onChange={e => updateField('phone', e.target.value)} /></div>
              </div>
              <div><label className="text-sm font-medium mb-1 block">Address</label><Input value={settings?.address || ''} onChange={e => updateField('address', e.target.value)} /></div>
              <div className="border-t border-[#E7E0D6] pt-4 mt-4">
                <p className="text-sm font-medium mb-3">Social Links</p>
                <div className="grid grid-cols-2 gap-4">
                  {['twitter', 'facebook', 'instagram', 'linkedin', 'youtube'].map(platform => (
                    <div key={platform}>
                      <label className="text-xs text-[#5B616E] mb-1 block capitalize">{platform}</label>
                      <Input value={settings?.social_links?.[platform] || ''} onChange={e => updateSocial(platform, e.target.value)} placeholder={`https://${platform}.com/...`} />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
